import type { Attachment, KnowledgeItem } from "./types";

const TEXT_EXTENSIONS = new Set([
  "txt", "md", "markdown", "json", "yaml", "yml", "toml", "xml", "csv", "tsv",
  "js", "jsx", "ts", "tsx", "css", "scss", "html", "py", "rb", "go", "rs", "java",
  "kt", "swift", "c", "h", "cpp", "hpp", "cs", "php", "sql", "sh", "ps1", "dockerfile",
]);

function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

export async function fileToAttachment(file: File): Promise<Attachment> {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  const base: Attachment = {
    id: crypto.randomUUID(),
    name: file.name,
    mimeType: file.type || "application/octet-stream",
    size: file.size,
  };

  if (extension === "docx") {
    const mammoth = (await import("mammoth/mammoth.browser")).default;
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return { ...base, extractedText: result.value };
  }

  if (file.type.startsWith("text/") || TEXT_EXTENSIONS.has(extension)) {
    return { ...base, extractedText: await readText(file) };
  }

  if (file.type.startsWith("image/") || file.type === "application/pdf") {
    return { ...base, dataUrl: await readDataUrl(file) };
  }

  return {
    ...base,
    extractedText: `[File binario allegato: ${file.name}, ${file.type || "tipo sconosciuto"}, ${file.size} byte]`,
  };
}

export async function fileToKnowledge(file: File): Promise<KnowledgeItem> {
  const attachment = await fileToAttachment(file);
  return {
    id: attachment.id,
    name: attachment.name,
    text:
      attachment.extractedText ??
      `[Contenuto multimodale ${attachment.mimeType}. Allegare il file alla chat per analizzarlo.]`,
    mimeType: attachment.mimeType,
    size: attachment.size,
    createdAt: Date.now(),
  };
}

export function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}
